// Check if the custom element is already defined to avoid redefining it
if (!customElements.get('selling-plan-picker')) {
  customElements.define(
    'selling-plan-picker',
    class SellingPlanPicker extends HTMLElement {
      constructor() {
        super();

        // Cache important DOM elements used across the component
        this.elements = {
          radioCards: this.querySelectorAll('.radio-card'),
          sellingPlanFrequency: this.querySelector('[data-selling-plan-frequency]'),
          sellingPlanPrices: this.querySelectorAll('[data-selling-plan-price]'),
          sellingPlanSavings: this.querySelector('[data-selling-plan-savings]'),
          sellingPlanSubtitle: this.querySelector('[data-selling-plan-subtitle]'),
          hiddenSellingPlanInput: this.querySelector('.selected-selling-plan-id'),
          oneTimeRadio: this.querySelector(`#${this.dataset.sectionId}_one_time_purchase`),
          subscriptionRadio: this.querySelector(`#${this.dataset.sectionId}_subscription_purchase`),
          subscriptionVariantIdInput: this.querySelector('.subscription-variant-id'),
        }

        // Parse embedded JSON data from script tag
        this.sellingPlanData = JSON.parse(
          this.querySelector("script[type='application/json']").textContent
        );

        // Get initial variant info from dataset
        this.currentVariant = JSON.parse(this.dataset.initialVariant);

        this.init();
      }

      init() {
        // Initial setup of frequency options
        this.updateFrequencyOptions(this.currentVariant.id);

        // Listen for variant changes on the product
        document.addEventListener(`${this.dataset.sectionId}-variant:change`, this.handleVariantChange.bind(this));

        // Listen to changes on the purchase type radio buttons
        if (this.elements.oneTimeRadio) this.elements.oneTimeRadio.addEventListener('change', () => {
          this.handleOneTimePurchase();
          this.updateDeoPdpDrawerContent();
        });
        if (this.elements.subscriptionRadio) this.elements.subscriptionRadio.addEventListener('change', () => {
          this.handleSubscriptionPurchase();
          this.updateDeoPdpDrawerContent();
        });
      }

      handleVariantChange(event) {
        // Update component state when the variant changes
        this.currentVariant = event.detail;

        this.updateFrequencyOptions(this.currentVariant.id);
        this.updatePrice();

        if (this.elements.oneTimeRadio.checked) {
          this.handleOneTimePurchase();
        }
        else if (this.elements.subscriptionRadio.checked) {
          this.handleSubscriptionPurchase();
        }
      }

      updateFrequencyOptions(variantId) {
        // Get selling plan options for the selected variant
        this.currentFrequencyOptions = this.sellingPlanData.variants.find(
          (variant) => variant.id === variantId
        )?.sellingPlanGroups;

        // If no subscription options are available, hide UI and clear inputs
        if (!this.currentFrequencyOptions) {
          this.classList.add('hide');
          this.clearSellingPlan();
          this.clearSubscriptionInputs();
          return;
        }

        this.classList.remove('hide');

        // Build <option> list for each frequency/selling plan group
        this.elements.sellingPlanFrequency.innerHTML =
          (this.currentFrequencyOptions || [])
            .map((frequency) => {
              if (!frequency || !frequency.sellingPlans || frequency.sellingPlans.length === 0) {
                return ''; // Skip empty groups
              }

              // Build readable key and fetch optional metadata
              const sellingPlanNameKey = handleize(frequency.sellingPlans[0].descriptionDefault || frequency.name || '').replaceAll('-', '_');
              const subtitle = this.sellingPlanData.selling_plan_names[sellingPlanNameKey]?.subtitle || '';
              const isMostPopular = frequency.sellingPlans[0].isMostPopular === true;
              const isPreferredFrequency = frequency.sellingPlans[0].isPreferredFrequency === true;
              const rechargeName = frequency.sellingPlans[0].description || frequency.name;

              return `
                <option value="${frequency.sellingPlans[0].id}" data-selling-plan-group-id="${frequency.id}" data-selling-plan-name="${frequency.name}" data-order-interval-frequency="${frequency.options[1].values[0]}" data-selling-plan-subtitle="${subtitle}" data-is-most-popular="${isMostPopular}" ${isPreferredFrequency ? 'selected' : ''}>
                  ${rechargeName}
                </option>
              `;
            })
            .filter(option => option !== '') // Remove empty items
            .join("");

        // Refresh UI component (likely a custom select plugin)
        const instance = EasySelect.get(this.elements.sellingPlanFrequency.id).refresh();

        // Determine which plan is currently selected
        let selectedOption = this.elements.sellingPlanFrequency.options[this.elements.sellingPlanFrequency.selectedIndex];
        if (!selectedOption) {
          selectedOption = this.elements.sellingPlanFrequency.options[0]; // Fallback
        }

        // Find full plan object based on selected ID
        const initialSellingPlanId = selectedOption.value;
        const initialSellingPlanGroup = this.currentFrequencyOptions.find(
          (group) => group.sellingPlans.some(plan => plan.id === parseInt(initialSellingPlanId))
        );
        const initialSellingPlan = initialSellingPlanGroup.sellingPlans.find(plan => plan.id === parseInt(initialSellingPlanId));

        this.updateSellingPlanDetails(initialSellingPlan);

        // Handle input value and field visibility depending on selected purchase type
        if (this.elements.subscriptionRadio) {
          if (this.elements.subscriptionRadio.checked) {
            this.setSellingPlan(initialSellingPlan);
            this.setSubscriptionInputs();
          } else {
            this.clearSellingPlan(initialSellingPlan);
            this.clearSubscriptionInputs();
          }
        } else {
          this.setSellingPlan(initialSellingPlan);
        }

        // Watch for future changes in frequency dropdown
        this.elements.sellingPlanFrequency.addEventListener(
          'change',
          this.handleFrequencyChange.bind(this)
        );
      }

      handleFrequencyChange(event) {
        // Handle user selection in frequency dropdown
        const selectedSellingPlanId = event.target.value;
        const selectedOption = event.target.options[event.target.selectedIndex];
        const selectedSellingGroupId = selectedOption.dataset.sellingPlanGroupId;

        // Locate the matching group and plan object
        const selectedSellingGroup = this.currentFrequencyOptions.find(
          (group) => group.id === selectedSellingGroupId
        );

        const selectedSellingPlan = selectedSellingGroup.sellingPlans[0];
        // Update hidden input dataset with orderIntervalFrequency from option
        if (this.elements.hiddenSellingPlanInput) {
          this.elements.hiddenSellingPlanInput.dataset.orderIntervalFrequency = selectedOption.dataset.orderIntervalFrequency || '';
        }

        this.updateSellingPlanDetails(selectedSellingPlan);

        // Update input values if subscription is selected
        if (this.elements.subscriptionRadio && this.elements.subscriptionRadio.checked) {
          this.setSellingPlan(selectedSellingPlan);
          this.setSubscriptionInputs();
        } else {
          this.setSellingPlan(selectedSellingPlan);
        }
      }

      updateSellingPlanDetails(sellingPlan) {
        // Update displayed price, savings, and subtitle

        if (this.elements.sellingPlanPrices && sellingPlan.price !== undefined) {
          this.elements.sellingPlanPrices.forEach(element => {
            element.innerHTML = sellingPlan.price;
          });
        }

        // Handle savings display for products with or without a one-time variant
        if (this.sellingPlanData.onetimeProduct) {
          const oneTimeProductSameVariant = this.sellingPlanData.onetimeProduct.variants.find(variant =>
            variant.title === this.currentVariant.title
          );

          if (oneTimeProductSameVariant) {
            this.elements.sellingPlanSavings.innerHTML = this.calculateSavings(
              this.currentVariant.price,
              oneTimeProductSameVariant.price
            );
          }
        } else {
          if (this.elements.sellingPlanSavings && this.currentVariant.price !== undefined && sellingPlan.price !== undefined) {
            const savings = this.calculateSavings(this.currentVariant.price, sellingPlan.price);
            this.elements.sellingPlanSavings.innerHTML = savings;
          }
        }

        // Show subtitle if available
        const sellingPlanNameKey = handleize(sellingPlan.descriptionDefault || sellingPlan.description).replaceAll('-', '_');
        const subtitle = this.sellingPlanData.selling_plan_names[sellingPlanNameKey]?.subtitle || '';

        this.elements.sellingPlanSubtitle.innerHTML = subtitle;
      }

      // Method to update drawer content when selling plan changes
      async updateDeoPdpDrawerContent() {
        const drawerElement = document.querySelector('drawer-extra-product');
        if (!drawerElement) return;

        // Get current section ID from drawer element
        const sectionId = drawerElement.dataset.section;
        if (!sectionId) return;

        // Determine which product to use
        const isOneTimeActive = this.elements.oneTimeRadio?.checked;
        let productHandle;
        
        if (isOneTimeActive && this.sellingPlanData.onetimeProduct) {
          productHandle = this.sellingPlanData.onetimeProduct.handle;
        } else {
          productHandle = this.sellingPlanData.handle;
        }

        if (!productHandle) return;

        try {
          // Fetch drawer content with selected product handle
          const locale = window.location.pathname.split('/products')[0];
          const response = await fetch(`${locale}/products/${productHandle}?section_id=${sectionId}`);
          const html = await response.text();
          
          // Parse and replace drawer content
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newDrawerContent = doc.querySelector('drawer-extra-product');
          
          if (newDrawerContent) {
            // Replace current drawer innerHTML
            drawerElement.innerHTML = newDrawerContent.innerHTML;
            
            // Re-initialize drawer-extra-product custom element
            if (drawerElement.connectedCallback) {
              drawerElement.connectedCallback();
            }
            
            // Re-attach close button event listeners
            this.reAttachCloseListeners();
            
            // Re-run the close text position script after content update
            if (typeof updateCloseTextPosition3 === 'function') {
              updateCloseTextPosition3();
            }
            
            // Toggle buttons based on drawer items
            if (typeof toggleAddToBagButtons === 'function') {
              toggleAddToBagButtons();
            }
          }
        } catch (error) {
          console.error('Error updating drawer content:', error);
        }
      }

      // Re-attach close button event listeners after innerHTML replacement
      reAttachCloseListeners() {
        // Close buttons with data-close attribute
        const closeButtons = document.querySelectorAll('#drawer-extra-product [data-close]');
        closeButtons.forEach(button => {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            if (typeof Woolman !== 'undefined' && Woolman.ModalsAndDrawers) {
              Woolman.ModalsAndDrawers.closeModalOrDrawer('drawer-extra-product');
            }
          });
        });

        // Close on overlay click (outside drawer)
        const overlay = document.querySelector('#drawer-extra-product .page-overlay');
        if (overlay) {
          overlay.addEventListener('click', (e) => {
            e.preventDefault();
            if (typeof Woolman !== 'undefined' && Woolman.ModalsAndDrawers) {
              Woolman.ModalsAndDrawers.closeModalOrDrawer('drawer-extra-product');
            }
          });
        }
      }

      // Set the selling plan ID on the hidden input
      setSellingPlan(sellingPlan) {
        this.elements.hiddenSellingPlanInput.dataset.sellingPlanName = sellingPlan.description
        this.elements.hiddenSellingPlanInput.value = sellingPlan.id;
        this.elements.hiddenSellingPlanInput.disabled = false;
      }

      // Clear the hidden input for selling plan
      clearSellingPlan() {
        this.elements.hiddenSellingPlanInput.value = '';
        this.elements.hiddenSellingPlanInput.disabled = true;
      }

      // Enable hidden subscription-specific input
      setSubscriptionInputs() {
        if (this.elements.subscriptionVariantIdInput) this.elements.subscriptionVariantIdInput.disabled = false;
      }

      // Disable subscription-specific input
      clearSubscriptionInputs() {
        if (this.elements.subscriptionVariantIdInput) this.elements.subscriptionVariantIdInput.disabled = true;
      }

      handleOneTimePurchase() {
        // Clear values and reset UI for one-time purchase
        this.clearSellingPlan();
        this.clearSubscriptionInputs();
        this.updateCardStyles();
        this.updatePrice();
      }

      handleSubscriptionPurchase() {
        // Get selected selling plan ID from dropdown
        const selectedOption =
          this.elements.sellingPlanFrequency.options[
            this.elements.sellingPlanFrequency.selectedIndex
          ];

        const selectedSellingPlanId = selectedOption
          ? selectedOption.value
          : this.currentFrequencyOptions[0].sellingPlans[0].id;

        // Locate full selling plan object
        const selectedSellingPlan = this.currentFrequencyOptions
          .map((group) => group.sellingPlans)
          .flat()
          .find((plan) => plan.id === parseInt(selectedSellingPlanId));

        this.setSellingPlan(selectedSellingPlan);
        this.setSubscriptionInputs();
        this.updateSellingPlanDetails(selectedSellingPlan);
        this.updateCardStyles();
        this.updatePrice();
      }

      // Calculate and return savings percentage
      calculateSavings(price, compareAtPrice) {
        const savingsPercentage =
          ((compareAtPrice - price) / compareAtPrice) * 100;
        return savingsPercentage.toFixed(0);
      }

      updateCardStyles() {
        if (!this.elements.oneTimeRadio || !this.elements.subscriptionRadio) return;

        // Reset active styling
        this.elements.radioCards.forEach((card) => {
          card.classList.remove('active');
        });

        // Add active style to selected option
        if (this.elements.oneTimeRadio.checked) {
          this.elements.oneTimeRadio.closest('.radio-card').classList.add('active');
        } else if (this.elements.subscriptionRadio.checked) {
          this.elements.subscriptionRadio.closest('.radio-card').classList.add('active');
        }
      }

      updatePrice() {
        // Fetch and replace product price HTML after changing variant or purchase type

        if (!this.sellingPlanData.onetimeProduct) return;

        const variantId = this.elements.oneTimeRadio.checked
          ? this.sellingPlanData.onetimeProduct.variants.find(variant => variant.title === this.currentVariant.title)?.id
          : this.currentVariant.id;

        const handle = this.elements.oneTimeRadio.checked
          ? this.sellingPlanData.onetimeProduct.handle
          : this.sellingPlanData.handle;

        if (!variantId) return;

        fetch(`${handle}?variant=${variantId}`)
          .then(response => {
            if (!response.ok) throw new Error('Network response was not ok');
            return response.text();
          })
          .then(responseText => {
            const html = new DOMParser().parseFromString(responseText, "text/html");
            const source = html.querySelector('.product-main .price');
            const price = document.querySelector('.product-main .price');

            if (source && price) {
              price.innerHTML = source.innerHTML;
            }
          })
          .catch(error => {
            console.error('Error fetching price:', error);
          });
      }
    }
  );
}
